import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { usePapaParse, useCSVDownloader } from 'react-papaparse';
import styled, { useTheme } from 'styled-components';
import { Col, Divider, Row, Upload } from 'antd5';
import { DownloadOutlined, InfoCircleTwoTone, UploadOutlined } from '@ant-design5/icons';
import { Button, WarningAlert, useMessage } from 'components/ui';

import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import { uploadIngestFiles, useGetIngestUploadUrls } from 'utils/ingest';
import { useUploadContext } from 'components/FileUploader';
import { CSV_BULK_UPLOAD_OPTIONS, JSON_BULK_UPLOAD_OPTIONS } from 'utils/constants';
import { EXAMPLE_INSPECTIONS, ExampleInspection } from '../constants';
import { FieldMappingForm } from '../FieldMappingForm';
import { Preview } from './Preview';
import { getBulkUploadEventTypes } from '../../utils';
import { InspectionUploadWorkflowComponentProps, Steps } from '../UploadInspectionDataModal';
import { ValidationProps } from './Validation';
import {
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  AtlasGqlInspectionTask,
  AtlasGqlMetadataFieldMapping,
  AtlasGqlSite,
  AtlasGqlTurbine,
  useGetInspectionMetadataFieldsQuery,
  useGetTasksForInspectionUploadQuery,
  useProcessInspectionMetadataFileMutation,
} from 'types/atlas-graphql';
import { getUniqueIdentifier } from 'horizon/components/Assets/AssetDetails/helpers';
import { getAssetPosition } from 'utils/asset-helpers';
import { isEmpty } from 'lodash';

export const MultipleInspections: React.FC<
  InspectionUploadWorkflowComponentProps & {
    setValidationStepProps: Dispatch<
      SetStateAction<
        Omit<ValidationProps, 'setStep' | 'setPendingStep' | 'setUploadErrors' | 'rollback'>
      >
    >;
  }
> = ({
  uploadId,
  requireTaskId,
  step,
  setStep,
  setUploadErrors,
  setValidationStepProps,
  setCreatedInspectionIds,
  inspectionTasks,
}) => {
  const theme = useTheme();
  const [file, setFile] = useState<File>();
  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>();
  const [previewRecord, setPreviewRecord] = useState<{ [key: string]: string }>({});
  const [fieldMappings, setFieldMappings] = useState<AtlasGqlMetadataFieldMapping[] | undefined>();
  const [inspectionCount, setInspectionCount] = useState<number>(0);
  const [fileType, setFileType] = useState<AtlasGqlFileFormat>(AtlasGqlFileFormat.Csv);
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  const [likelyDelimiterError, setLikelyDelimiterError] = useState<boolean>(false);
  const [cellDelimiter, setCellDelimiter] = useState<AtlasGqlFileFieldDelimiter>(
    AtlasGqlFileFieldDelimiter.Comma
  );
  const [decimalDelimiter, setDecimalDelimiter] = useState<AtlasGqlDecimalSeparator>(
    AtlasGqlDecimalSeparator.Period
  );

  const { refetch } = useServerDataTableContext().context;

  const { message } = useMessage();
  const { readString } = usePapaParse();
  const { setSelectedFiles, setUploading } = useUploadContext();

  const { data } = useGetInspectionMetadataFieldsQuery();
  const internalFields = useMemo(() => {
    const fields = data?.inspectionMetadataFields ?? [];

    if (requireTaskId) {
      return fields.map(field =>
        field.internalName === 'taskId' ? { ...field, required: true } : field
      );
    }

    return fields;
  }, [data, requireTaskId]);

  const idsOfTasksWithoutInspections = (inspectionTasks ?? [])
    .filter(({ inspectionId }) => !inspectionId)
    .map(({ taskId }) => taskId);
  const inspectionTasksWithInspectionIds: { taskId: string; inspectionId: string }[] = (
    inspectionTasks ?? []
  ).filter((t): t is { taskId: string; inspectionId: string } => !!t.inspectionId);

  const { data: existingTaskData } = useGetTasksForInspectionUploadQuery({
    variables: {
      filterBy: [{ key: 'id', values: idsOfTasksWithoutInspections }],
    },
    skip: !idsOfTasksWithoutInspections.length,
  });
  const hasExistingTaskData = existingTaskData?.paginatedTasks?.items.length;

  const { exampleCsv, exampleJson } = useMemo(() => {
    if (!data) {
      return { exampleCsv: '', exampleJson: '' };
    }

    const fields = data.inspectionMetadataFields;

    let exampleInspections: ExampleInspection[] = EXAMPLE_INSPECTIONS;
    if (hasExistingTaskData) {
      exampleInspections = existingTaskData.paginatedTasks!.items.map(
        ({ id, asset }: AtlasGqlInspectionTask) => ({
          id: '',
          description: '',
          inspectionDate: '',
          taskId: id,
          typeName: '',
          siteName:
            (asset.ancestors?.find(a => a?.assetType?.name === 'Site') as AtlasGqlSite)?.name ?? '',
          turbineName:
            (asset.assetType?.name === 'Turbine'
              ? (asset as AtlasGqlTurbine).name
              : (asset?.ancestors?.find(a => a?.assetType?.name === 'Turbine') as AtlasGqlTurbine)
                  ?.name) ?? '',
          assetType: asset.assetType?.name ?? '',
          asset:
            asset.assetType?.name === 'Turbine'
              ? ''
              : !isEmpty(getAssetPosition(asset))
                ? getAssetPosition(asset)
                : getUniqueIdentifier(asset),
          vendor: '',
        })
      );
    }

    const exampleCsv =
      fields.map(field => field.defaultCsvHeader ?? field.internalName).join(',') +
      '\n' +
      exampleInspections
        .map(inspection =>
          fields.map(field => inspection[field.internalName as keyof typeof inspection]).join(',')
        )
        .join('\n');
    const exampleData = exampleInspections.map(inspection =>
      Object.fromEntries(
        fields.map(field => [
          field.defaultJsonFieldName ?? field.internalName,
          inspection[field.internalName as keyof typeof inspection],
        ])
      )
    );
    const exampleJson = `data:application/json,${JSON.stringify(exampleData)}`;

    return { exampleCsv, exampleJson };
  }, [data, existingTaskData]);

  const parseFile = useCallback(
    async (file: File) => {
      if (file.type === 'text/csv') {
        const csvText = await file.text();

        const {
          data,
          meta,
        }: {
          data: { [key: string]: string }[];
          meta: { delimiter: string; fields: string[]; [key: string]: any };
        } = readString(csvText, {
          header: true,
          preview: 2,
          skipEmptyLines: true,
          /**
           * Papaparse will auto-detect the delimiter by default, but passing it
           * explicitly so the user will be forced to select the cell delimiter
           * before firing the mutation (to ensure the correct delimiter is passed).
           */
          delimiter: {
            COMMA: ',',
            SEMICOLON: ';',
            TAB: '\t',
          }[cellDelimiter],
        });

        setProvidedFieldNames(meta.fields);
        setPreviewRecord(data[0]);
        setInspectionCount(data.length);
        setFileType(AtlasGqlFileFormat.Csv);
        setLikelyDelimiterError(meta.fields.length === 1);
      } else if (file.type === 'application/json') {
        const jsonText = await file.text();

        try {
          const parsedJson = JSON.parse(jsonText);
          /**
           * This assumes (for now) that all records are the same format and are
           * simple key/value pairs. Based on the requirements, this seems fine
           * for now (especially since we have an example file). We may want to support
           * more options in the future.
           */
          if (Array.isArray(parsedJson) && parsedJson.length) {
            setProvidedFieldNames(Object.keys(parsedJson[0]));
            setPreviewRecord(parsedJson[0]);
            setInspectionCount(parsedJson.length);
            setFileType(AtlasGqlFileFormat.Json);
          }
        } catch (error) {
          message.error('Error parsing JSON. Please verify formatting and try again.');
        }
      } else {
        message.error('File type not supported. Please upload a .csv or .json file.');
      }
    },
    [cellDelimiter]
  );

  const handleUpload = ({ file }: { file: File; fileList: File[] }) => {
    setFile(file);
    parseFile(file);
  };

  useEffect(() => {
    if (file) {
      parseFile(file);
    }
  }, [cellDelimiter]);

  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  useEffect(() => {
    if (providedFieldNames?.length) {
      setStep(Steps.SummaryMapping);
    }
  }, [providedFieldNames]);

  const { getIngestUploadUrls } = useGetIngestUploadUrls();
  const [processMetadataFile] = useProcessInspectionMetadataFileMutation();

  const handleComplete = async () => {
    if (file) {
      setUploading(true);

      const dataType = inspectionUploadEventTypes.inspectionType;
      const fileName = file.name;

      // Get signed url for upload
      const { items } = await getIngestUploadUrls({
        dataType,
        files: [{ fileName }],
        prefix: dataType,
        recordId: uploadId,
      });
      const [uploadItem] = items;

      if (!uploadItem) {
        message.error('Error uploading inspections: Upload URL could not be obtained');
        setUploading(false);
        return;
      }

      setSelectedFiles([
        {
          name: fileName,
          path: '',
          percent: 0,
          sourceName: fileName,
          type: 'file',
          valid: true,
        },
      ]);

      try {
        // Upload file to S3
        await uploadIngestFiles({
          items: [{ ...uploadItem, file }],
        });
      } catch (error) {
        message.error(`Error uploading inspections: ${error.message}`);
        setUploading(false);
        return;
      }

      // Once the file is in S3, call mutation to process
      const { data } = await processMetadataFile({
        variables: {
          input: {
            uploadId,
            key: uploadItem.fields.key,
            bucket: uploadItem.fields.bucket,
            fileFormat: fileType,
            decimalSeparator: decimalDelimiter,
            delimiter: cellDelimiter,
            fieldMappings,
          },
        },
      });
      setUploading(false);

      // conditonally refetch the inspection list
      refetch && (await refetch());

      setValidationStepProps({
        processInspectionMetadataFile: data?.processInspectionMetadataFile,
        fileName: file?.name ?? 'your summary file',
      });
      setStep(Steps.SummaryValidation);
      setUploadErrors(uploadErrors => ({
        ...uploadErrors,
        summary: (data?.processInspectionMetadataFile?.failureCount ?? 0) > 0,
      }));
      setCreatedInspectionIds(
        (data?.processInspectionMetadataFile?.inspections ?? []).map(({ id }) => id)
      );
    }
  };

  const { CSVDownloader } = useCSVDownloader();
  // Declaring a styled component inside component because CSVDownloader must be desctructured from useCSVDownloader
  const StyledCSVDownloader = styled(CSVDownloader)<{ marginRight?: string }>`
    background: transparent;
    border: 0;

    ${({ marginRight }) => marginRight && `margin-right: ${marginRight}`}
  `;

  return (
    <div>
      {[Steps.SummaryUpload, Steps.SummaryMapping].includes(step) && (
        <>
          {inspectionTasksWithInspectionIds.length ? (
            <p>
              <InfoCircleTwoTone twoToneColor={theme.lightBlue} />{' '}
              <span>
                {inspectionTasksWithInspectionIds.length} selected Task
                {inspectionTasksWithInspectionIds.length > 1
                  ? 's already have associated Inspections'
                  : ' already has an associated Inspection'}
                .
              </span>{' '}
              You may add Images to existing Inspections in an upcoming step.
            </p>
          ) : null}
          {idsOfTasksWithoutInspections.length ? (
            <p>
              {' '}
              <strong>
                Create Inspection
                {idsOfTasksWithoutInspections.length > 1 ? 's' : ''} for{' '}
                {idsOfTasksWithoutInspections.length} Task
                {idsOfTasksWithoutInspections.length > 1 ? 's' : ''}
              </strong>
              <br />
            </p>
          ) : null}
          <p>
            An Inspection Summary file is required for bulk inspection upload. This file provides
            inspection-level data such as Asset relationships, description, and date.
          </p>

          {!hasExistingTaskData && !!(exampleCsv.length || exampleJson.length) && (
            <p>
              Click to download a sample summary file in{' '}
              <StyledCSVDownloader data={exampleCsv} filename={'summary_example'} type={'link'}>
                csv
              </StyledCSVDownloader>{' '}
              or{' '}
              <a download="summary_example.json" href={exampleJson}>
                json
              </a>
              .
            </p>
          )}
        </>
      )}

      {step === Steps.SummaryUpload && (
        <>
          <Upload
            accept={'.csv,.json'}
            beforeUpload={() => false} // bypass antd callbacks; handle change ourselves
            // @ts-ignore - tedious antd types; this is fine
            onChange={handleUpload}
          >
            <Button className="testid-upload-multiple-inspection-summary" icon={<UploadOutlined />}>
              Upload Summary
            </Button>
          </Upload>
          <br />
          {hasExistingTaskData && (
            <>
              <p>
                If you do not have a summary file to create Inspections for these Tasks, you can
                generate one to edit that is pre-populated with Task and Asset information using one
                of the options below.
                <br />
                <br />
                <StyledCSVDownloader
                  data={exampleCsv}
                  filename={'summary'}
                  type={'link'}
                  marginRight={'0.5rem'}
                >
                  <Button icon={<DownloadOutlined />}>summary.csv</Button>
                </StyledCSVDownloader>
                <Button icon={<DownloadOutlined />} download="summary.json" href={exampleJson}>
                  summary.json
                </Button>
              </p>
            </>
          )}
          {requireTaskId && (
            <WarningAlert message="Rows providing details for Inspection Tasks that already have an Inspection linked will be discarded." />
          )}
        </>
      )}
      {step === Steps.SummaryMapping && (
        <>
          <Divider />
          <Row gutter={{ sm: 16 }}>
            <Col flex="50%">
              <h3>
                Selected file: <strong>{file?.name}</strong>
              </h3>
              <p>Select a source column for each Summary attribute</p>
            </Col>
          </Row>
          <FieldMappingForm
            fieldMappings={fieldMappings}
            fileType={fileType}
            internalFields={internalFields}
            providedFieldNames={providedFieldNames ?? []}
            previousStep={Steps.SummaryUpload}
            nextStep={Steps.SummaryPreview}
            setStep={setStep}
            setFieldMappings={setFieldMappings}
            likelyDelimiterError={likelyDelimiterError}
            cellDelimiter={cellDelimiter}
            setCellDelimiter={setCellDelimiter}
            decimalDelimiter={decimalDelimiter}
            setDecimalDelimiter={setDecimalDelimiter}
          />
        </>
      )}
      {step === Steps.SummaryPreview && fieldMappings && (
        <Preview
          inspectionCount={inspectionCount}
          internalFields={internalFields}
          fieldMappings={fieldMappings}
          record={previewRecord}
          setStep={setStep}
          handleComplete={handleComplete}
        />
      )}
    </div>
  );
};
